import { useEffect, useMemo, useState, useCallback } from "react";
import {
  Radio,
  Select,
  DatePicker,
  message,
  Checkbox,
  Button,
  Popconfirm,
} from "antd";
import ApplicationCard from "../../../components/application-card";
import { asyncGetApplicationGroups } from "../../../store/easyApp/easyapp-async";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { setProperty } from "../../../store/properties/property-slice";
import { joinApplicationGroupsService } from "../../../services/easyapp.service";
import { Loader } from "../../../components";
import dayjs, { Dayjs } from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import styles from "./easy-app.module.scss";
import { DATE_FILTER_OPTIONS, STATUS_FILTER_MAP, STATUS_OPTIONS } from "./easyapp.constants";

dayjs.extend(isBetween);

function EasyApp(): React.ReactElement {
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [selectedPropID, setSelectedPropID] = useState(() => {
    // Restore property selection from sessionStorage on mount
    return sessionStorage.getItem("selectedEasyAppPropertyId") || "";
  });
  const [selectedDateFilter, setSelectedDateFilter] = useState<string | null>(null);
  const [customDateRange, setCustomDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [applicationStatus, setApplicationStatus] = useState("pending");
  const [isJoinMode, setIsJoinMode] = useState(false);
  const [selectedApps, setSelectedApps] = useState<Set<string>>(new Set());

  const dispatch = useAppDispatch();
  const { RangePicker } = DatePicker;

  const user = useAppSelector((state) => state.user);
  const userProperty = useAppSelector((state) => state.user?.userProperty || []);
  const appGroups = useAppSelector((state) => state.easyapp?.appGroups || []);
  const userInfo: any = user?.user;

  const propertyOptions = useMemo(
    () =>
      (userProperty as any[]).map((property) => ({
        value: property.property_id,
        label: property.property_name,
      })),
    [userProperty]
  );

  const isWithinDateRange = useCallback(
    (date: Date | null, filterType: string): boolean => {
      if (!date) return false;

      try {
        const submittedDate = dayjs(date);
        const today = dayjs();

        switch (filterType) {
          case "today":
            return submittedDate.isSame(today, "day");
          case "yesterday":
            return submittedDate.isSame(today.subtract(1, "day"), "day");
          case "this_week":
            return (
              submittedDate.isAfter(today.startOf("week")) ||
              submittedDate.isSame(today.startOf("week"), "day")
            );
          case "this_month":
            return (
              submittedDate.isSame(today, "month") &&
              submittedDate.isSame(today, "year")
            );
          case "last_month": {
            const lastMonth = today.subtract(1, "month");
            return (
              submittedDate.isSame(lastMonth, "month") &&
              submittedDate.isSame(lastMonth, "year")
            );
          }
          case "custom":
            if (!customDateRange?.[0] || !customDateRange?.[1]) return true;
            return submittedDate.isBetween(
              customDateRange[0],
              customDateRange[1],
              null,
              "[]"
            );
          default:
            return true;
        }
      } catch {
        return true;
      }
    },
    [customDateRange]
  );

  const filteredApplications = useMemo(() => {
    if (!Array.isArray(appGroups)) return [];

    const allowedStatuses = STATUS_FILTER_MAP[applicationStatus] || [];

    return appGroups.filter((appGroup: any) => {
      if (!appGroup) return false;

      const matchesProperty = selectedPropID
        ? appGroup.ms_property_id === selectedPropID
        : true;
      const matchesDate = selectedDateFilter
        ? isWithinDateRange(appGroup.submitted_at, selectedDateFilter)
        : true;
      const matchesStatus = allowedStatuses.includes(appGroup.status);

      return matchesProperty && matchesDate && matchesStatus;
    });
  }, [
    appGroups,
    applicationStatus,
    selectedPropID,
    selectedDateFilter,
    customDateRange,
    isWithinDateRange,
  ]);

  const canJoin = useMemo(() => {
    if (selectedApps.size < 2) return false;

    const selectedAppList = filteredApplications.filter((app: any) =>
      selectedApps.has(app.application_group)
    );

    if (selectedAppList.length < 2) return false;

    const firstPropId = selectedAppList[0].ms_property_id;
    return selectedAppList.every((app: any) => app.ms_property_id === firstPropId);
  }, [selectedApps, filteredApplications]);

  const withFilterLoading = useCallback((callback: () => void) => {
    setFilterLoading(true);
    callback();
    setTimeout(() => setFilterLoading(false), 200);
  }, []);

  const handleRadioChange = useCallback(
    (e: any) => {
      const value = e?.target?.value;
      if (value) {
        withFilterLoading(() => setApplicationStatus(value));
      }
    },
    [withFilterLoading]
  );

  const handlePropertyChange = useCallback(
    (propID: string) => {
      withFilterLoading(() => {
        setSelectedPropID(propID);
        // Persist property selection to sessionStorage
        if (propID) {
          sessionStorage.setItem("selectedEasyAppPropertyId", propID);
        } else {
          sessionStorage.removeItem("selectedEasyAppPropertyId");
        }
      });
    },
    [withFilterLoading]
  );

  const handleDateFilterChange = useCallback(
    (value: string) => {
      if (value !== "custom") {
        withFilterLoading(() => {
          setSelectedDateFilter(value);
          setCustomDateRange(null);
        });
      } else {
        setSelectedDateFilter(value);
      }
    },
    [withFilterLoading]
  );

  const handleCustomDateChange = useCallback(
    (dates: [Dayjs | null, Dayjs | null] | null) => {
      if (dates?.[0] && dates?.[1]) {
        withFilterLoading(() => {
          setCustomDateRange([dates[0] as Dayjs, dates[1] as Dayjs]);
          setSelectedDateFilter("custom");
        });
      }
    },
    [withFilterLoading]
  );

  const handleAppSelect = useCallback((appGroupId: string) => {
    setSelectedApps((prev) => {
      const next = new Set(prev);
      if (next.has(appGroupId)) {
        next.delete(appGroupId);
      } else {
        next.add(appGroupId);
      }
      return next;
    });
  }, []);

  const handleJoinApplications = async () => {
    try {
      const selectedAppList = Array.from(selectedApps);

      if (selectedAppList.length < 2) {
        message.error("Select at least 2 applications");
        return;
      }

      const [targetGroupId, ...applicationIds] = selectedAppList;

      const response: any = await joinApplicationGroupsService({
        application_group_id: targetGroupId,
        applicationGroupIds: applicationIds,
      });

      if (response?.data?.success) {
        message.success("Applications joined successfully");
        setSelectedApps(new Set());
        setIsJoinMode(false);

        // Refetch applications
        if (userInfo?.localAccountId) {
          try {
            setLoading(true);
            await dispatch(asyncGetApplicationGroups(userInfo.localAccountId));
          } catch (error) {
            message.error("Failed to refetch applications");
          } finally {
            setLoading(false);
          }
        }
      } else {
        message.error("Failed to join applications");
      }
    } catch (error) {
      message.error("Failed to join applications");
    }
  };

  const handleCancelJoin = useCallback(() => {
    setSelectedApps(new Set());
    setIsJoinMode(false);
  }, []);

  const handleToggleJoinMode = useCallback(() => {
    setIsJoinMode((prev) => !prev);
    if (isJoinMode) {
      setSelectedApps(new Set());
    }
  }, [isJoinMode]);

  // Load applications on mount
  useEffect(() => {
    const loadApplications = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      if (Array.isArray(appGroups) && appGroups.length > 0) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        if (userInfo?.localAccountId) {
          await dispatch(asyncGetApplicationGroups(userInfo.localAccountId));
        }
      } catch (error) {
        message.error("Failed to load applications");
      } finally {
        setLoading(false);
      }
    };

    loadApplications();
  }, [dispatch, user, appGroups.length, userInfo?.localAccountId]);

  useEffect(() => {
    dispatch(setProperty({ tabs: ["EasyApp"] }));
  }, [dispatch]);

  if (loading) {
    return <Loader />;
  }

  const selectedCount = selectedApps.size;
  const applicationCount = filteredApplications.length;

  return (
    <div className={styles.container}>
      {/* Filter Header */}
      <div className={styles.header}>
        <div className={styles.filterSection}>
          <h3 className={styles.heading}>Applications</h3>
          <Radio.Group
            options={STATUS_OPTIONS}
            defaultValue="pending"
            optionType="button"
            buttonStyle="solid"
            onChange={handleRadioChange}
            size="middle"
            disabled={isJoinMode}
          />
        </div>

        <div className={styles.filterControls}>
          {applicationStatus === 'pending' && (
            <Button type={isJoinMode ? "primary" : "default"} onClick={handleToggleJoinMode}>
              {isJoinMode ? 'Exit Join Mode' : 'Join Applications'}
            </Button>
          )}

          {selectedDateFilter === "custom" && (
            <RangePicker
              onChange={handleCustomDateChange}
              value={customDateRange}
              format="YYYY-MM-DD"
              className={styles.rangePicker}
            />
          )}

          <Select
            placeholder="Application Date"
            value={selectedDateFilter || undefined}
            options={DATE_FILTER_OPTIONS}
            onChange={handleDateFilterChange}
            allowClear
          />

          <Select
            placeholder="Select Property"
            value={selectedPropID || undefined}
            options={propertyOptions}
            onChange={handlePropertyChange}
            allowClear
            style={{ minWidth: 250 }}
            popupMatchSelectWidth={false}
            dropdownStyle={{ minWidth: 250 }}
          />
        </div>
      </div>

      {/* Application Count */}
      <p className={styles.applicationCount}>{applicationCount} applications</p>

      {/* Applications List */}
      {filterLoading ? (
        <Loader />
      ) : (
        <div className={styles.content}>
          {applicationCount > 0 ? (
            filteredApplications.map((application: any) => (
              <div key={application.application_group} className={styles.appRow}>
              <div
                className={`${styles.appCheckboxWrapper} ${
                  isJoinMode ? styles.visible : ""
                }`}
              >
                <Checkbox
                  checked={selectedApps.has(application.application_group)}
                  onChange={() => handleAppSelect(application.application_group)}
                />
              </div>
              <div className={styles.appCardWrapper}>
                <ApplicationCard application={application} />
              </div>
            </div>
            ))
          ) : (
            <p className={styles.emptyState}>No applications found</p>
          )}
        </div>
      )}

      {/* Sticky Join Bar */}
      <div className={styles.stickyContainer}>
        <div
          className={`${styles.stickyBar} ${
            isJoinMode && selectedCount > 0 ? styles.active : ""
          }`}
        >
          <div className={styles.topRow}>
            <span className={styles.selectionInfo}>
              {selectedCount} Application{selectedCount !== 1 ? "s" : ""} Selected
            </span>
            <div className={styles.buttonGroup}>
              <Button onClick={handleCancelJoin}>Cancel</Button>
              <Popconfirm
                title="Join Applications"
                description="Join selected applications?"
                onConfirm={handleJoinApplications}
                okText="Yes"
                cancelText="No"
              >
                <Button type="primary" disabled={!canJoin}>
                  Join
                </Button>
              </Popconfirm>
            </div>
          </div>

          <div
            className={`${styles.errorMessage} ${
              !canJoin && selectedCount >= 2 ? styles.visible : ""
            }`}
          >
            Application groups must be at the same property to be joined
          </div>
        </div>
      </div>
    </div>
  );
}

export default EasyApp;